/**
 * GFG-194 (Send Queue, GFG Admin) — unit U12. Read-only preview of the email a queued send would
 * deliver, opened from the row menu's "View email" action. Reuses `EmailPreview` — the same
 * renderer the campaign builder's Design Step uses for this exact `Stage3EmailState` shape —
 * rather than building a second renderer. No `onChooseGift` is passed: there is nothing to act on
 * from an admin's read-only view.
 *
 * MUI Dialog supplies the behaviour (focus trap, portal). Its default `onClose` wiring is left
 * un-overridden, so Escape and a backdrop click both close the dialog for free. The preview area
 * itself is Tailwind, per D21.
 */
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
} from "@mui/material";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import { COPY } from "@/constants/sendQueueCampaigns";
import { useSendQueueEmail } from "@/hooks/useSendQueueCampaigns";
import type { SendQueueItem } from "@/interfaces/sendQueueCampaigns";
import { apiErrorMessage } from "@/utils/apiError-helper";

interface ViewEmailDialogProps {
  /**
   * The row this dialog previews. May be null between the row menu closing and the parent
   * clearing its selection — `open` is what actually gates the fetch (via `useSendQueueEmail`'s
   * `enabled` argument), so a stale/null item never issues a request on its own.
   */
  item: SendQueueItem | null;
  open: boolean;
  onClose: () => void;
}

export default function ViewEmailDialog({ item, open, onClose }: ViewEmailDialogProps) {
  const { data, isLoading, isError, error } = useSendQueueEmail(item?.id ?? 0, open);
  /**
   * `item` may be null while `open` is true (see the prop's own doc comment), which disables the
   * query -- neither `isLoading` nor `isError` nor `data` reflects that transient state on its
   * own, so it is folded into the loading treatment rather than left unmatched (which previously
   * rendered a blank dialog body).
   */
  const isPending = isLoading || item == null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {COPY.emailDialog.title}
          <IconButton onClick={onClose} aria-label={COPY.closeLabel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        className="bg-wax flex justify-center items-start"
        sx={{ minHeight: 420, py: 4 }}
      >
        {isPending && (
          <div className="w-full max-w-[600px] flex flex-col gap-4" aria-busy="true">
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={130} />
            <Skeleton variant="rounded" height={260} />
          </div>
        )}
        {!isPending && isError && (
          <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
            {apiErrorMessage(error)}
          </Alert>
        )}
        {!isPending && !isError && data && <EmailPreview state={data} isMobile={false} />}
      </DialogContent>
    </Dialog>
  );
}
